import { makeAutoObservable, runInAction } from 'mobx'
import { type AxiosResponse, type AxiosInstance } from 'axios'
import { CachedClient, LazyAggregates, getAggregationString } from 'helpers'
import {
  type JSONObject,
  type EnergyPlantCarrier,
  type EnergyPlantSimulation,
  type EnergyPlantSimulationAggregate,
  type EnergyPlantUserData,
  type DeprecatedEnergyPlantUserData
} from 'stores/@types'
import {
  type AppFeature,
  FeatureType,
  type ParentFeature,
  type Project,
  type MultiFeature,
  type WriteFeature
} from 'stores/features'
import type AppStore from 'stores/AppStore'

export class EnergyPlant implements AppFeature, WriteFeature {
  public readonly type = FeatureType.EnergyPlant

  private readonly cached: CachedClient
  public readonly isEditingSupported: boolean = true

  constructor(
    private readonly project: Project,
    public uuid: string
  ) {
    makeAutoObservable(this, { uuid: false })
    this.cached = new CachedClient(this)
  }

  reset(): void {
    this.cached.reset()
  }

  get title(): string {
    return this.plantTypeTitle ?? 'Ein Objekt ausgewählt.'
  }

  get plantTypeTitle(): string | undefined {
    return this.simulation?.plant_type === undefined
      ? undefined
      : this.project.store.plantTypes?.get(this.simulation?.plant_type)?.title
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get monitorData(): JSONObject | undefined {
    return this.simulation
  }

  get simulation(): EnergyPlantSimulation | undefined {
    return this.cached.get(`energyplantsimulation/${this.uuid}/`)
  }

  get userData(): EnergyPlantUserData | undefined {
    return this.cached.get(`energyplant/${this.uuid}/`)
  }

  get carriers(): EnergyPlantCarrier[] | undefined {
    return this.cached.get(`energyplant/${this.uuid}/source/`)
  }

  get formPlaceHolder(): JSONObject | undefined {
    if (
      this.simulation === undefined ||
      this.userData === undefined ||
      this.carriers === undefined
    )
      return {}

    const firstCarrier = this.carriers[0]
    return {
      ...this.simulation,
      ...this.userData,
      energyCarrier: firstCarrier.carrier ?? null,
      energyCarrierUnit: firstCarrier.unit
    }
  }

  async patch(data: DeprecatedEnergyPlantUserData): Promise<AxiosResponse> {
    const promise = this.client.patch(`energyplant/${this.uuid}/`, data)
    void promise.then(() => {
      this.reset()
    })
    return await promise
  }
}

export class MultiEnergyPlant implements MultiFeature {
  public _caches: Record<string, Array<Record<string, number>> | undefined> = {}

  public readonly type = FeatureType.EnergyPlant

  public aggregates: LazyAggregates
  public readonly isEditingSupported: boolean = false

  private _count?: number
  private _simulation?: EnergyPlantSimulationAggregate
  private _sample?: EnergyPlant

  constructor(public readonly parent: ParentFeature) {
    this.aggregates = new LazyAggregates(this, 'energyplantsimulation')
    makeAutoObservable(this)
  }

  reset(): void {
    this._count = undefined
    this._simulation = undefined
    this._sample = undefined
    this._caches = {}
  }

  get count(): number | undefined {
    if (this._count !== undefined) return this._count
    void this.parent.client
      .get(`energyplantsimulation/?${this.parent.filter}aggregate=uuid=count`)
      .then((res) => runInAction(() => (this._count = res.data.uuid__count)))
  }

  get simulation(): EnergyPlantSimulationAggregate | undefined {
    if (this._simulation !== undefined) return this._simulation
    void this.parent.client
      .get(
        `energyplantsimulation/?${this.parent.filter}` +
          getAggregationString({
            output_power: 'sum',
            output_heat: 'sum'
          })
      )
      .then((res) => runInAction(() => (this._simulation = res.data)))
  }

  get sample(): EnergyPlant | undefined {
    if (this._sample !== undefined) return this._sample
    void this.parent.client
      .get(`energyplantsimulation/?${this.parent.filter}limit=1`)
      .then((res) =>
        runInAction(
          () =>
            (this._sample = new EnergyPlant(
              this.parent.store.selectedProject,
              res.data[0].uuid
            ))
        )
      )
  }
}

export class EnergyPlantCollection implements ParentFeature {
  public readonly type = FeatureType.EnergyPlant
  public energyPlants: MultiEnergyPlant
  public store: AppStore
  public readonly isEditingSupported: boolean = false

  constructor(
    private readonly project: Project,
    public uuids: string[]
  ) {
    this.energyPlants = new MultiEnergyPlant(this)
    makeAutoObservable(this)
    this.store = project.store
  }

  reset(): void {
    this.energyPlants.reset()
  }

  get title(): string {
    return `${this.uuids.length} Versorgungsanlagen ausgewählt`
  }

  get client(): AxiosInstance {
    return this.project.client
  }

  get filter(): string {
    return `uuid__in=${this.uuids.join(',')}&`
  }

  get monitorData(): JSONObject | undefined {
    return this.energyPlants.simulation
  }
}
